import { useAuthStore } from '../stores/authStore'

// Streams a RAG query response as SSE using fetch + ReadableStream. Calls
// `onEvent` for each parsed event ({ type, ... }). Resolves when the stream
// closes. Throws ApiError on non-2xx.
export async function streamQuery({ questionText, initiativeId, onEvent, signal }) {
  // Auth rides the Devise session cookie (credentials: 'same-origin'). No
  // bearer token — JWT was removed in Phase 1.5. This is a state-changing
  // POST, so it needs the CSRF token Rails enforces (protect_from_forgery),
  // same as services/api.js does for non-GET requests.
  const csrf = typeof document !== 'undefined'
    ? document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    : null

  const response = await fetch('/api/v1/queries', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
    },
    body: JSON.stringify({
      question_text: questionText,
      initiative_id: initiativeId || undefined,
    }),
    credentials: 'same-origin',
    signal,
  })

  if (!response.ok) {
    if (response.status === 401) {
      useAuthStore.getState().clearSession()
      if (typeof window !== 'undefined') window.location.href = '/login'
    }
    let message = `Query failed: ${response.status}`
    try {
      const body = await response.json()
      if (body.error) message = body.error
    } catch {
      // ignore
    }
    throw new Error(message)
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder('utf-8')
  let buffer = ''

  while (true) {
    const { value, done } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })

    let eventEnd
    while ((eventEnd = buffer.indexOf('\n\n')) !== -1) {
      const rawEvent = buffer.slice(0, eventEnd)
      buffer = buffer.slice(eventEnd + 2)

      const dataLines = rawEvent
        .split('\n')
        .filter((line) => line.startsWith('data: '))
        .map((line) => line.slice(6))

      if (dataLines.length === 0) continue
      const data = dataLines.join('\n')

      try {
        const parsed = JSON.parse(data)
        onEvent?.(parsed)
      } catch {
        // Ignore malformed JSON
      }
    }
  }
}
